/*
  Сравнение двух состояний программы для отчёта руководству.
*/

import type { EditableProject, AgileRisk } from '@/store/projectStore';
import type { GanttItem } from '@/data/projects';
import { SNAPSHOT_SCHEMA_VERSION, type ReviewSnapshotPayload } from '@/store/reviewSnapshotStore';
import { computeProjectProgress } from '@/store/projectStore';

export interface ProjectDiffRow {
  projectId: string;
  title: string;
  /** Было только в базе / только в сравнении */
  presence: 'both' | 'baseline_only' | 'current_only';
  progressWas: number | null;
  progressNow: number | null;
  statusWas: string | null;
  statusNow: string | null;
  phaseLabelWas: string | null;
  phaseLabelNow: string | null;
  openRisksWas: number | null;
  openRisksNow: number | null;
  ganttTasksWas: number | null;
  ganttTasksNow: number | null;
}

function openRiskCount(risks: AgileRisk[]): number {
  return risks.filter((r) => r.status === 'open').length;
}

function projectMap(payload: ReviewSnapshotPayload): Map<string, EditableProject> {
  return new Map(payload.projects.map((p) => [p.id, p]));
}

export function diffProgramState(
  baseline: ReviewSnapshotPayload,
  current: ReviewSnapshotPayload,
): ProjectDiffRow[] {
  const a = projectMap(baseline);
  const b = projectMap(current);
  const ids = new Set<string>([...Array.from(a.keys()), ...Array.from(b.keys())]);
  const rows: ProjectDiffRow[] = [];

  for (const id of Array.from(ids)) {
    const pa = a.get(id);
    const pb = b.get(id);
    let presence: ProjectDiffRow['presence'] = 'both';
    if (pa && !pb) presence = 'baseline_only';
    if (!pa && pb) presence = 'current_only';

    const title = pb?.title ?? pa?.title ?? id;

    rows.push({
      projectId: id,
      title,
      presence,
      progressWas: pa ? pa.progress : null,
      progressNow: pb ? pb.progress : null,
      statusWas: pa?.status ?? null,
      statusNow: pb?.status ?? null,
      phaseLabelWas: pa?.phaseLabel ?? null,
      phaseLabelNow: pb?.phaseLabel ?? null,
      openRisksWas: pa ? openRiskCount(pa.risks) : null,
      openRisksNow: pb ? openRiskCount(pb.risks) : null,
      ganttTasksWas: pa ? pa.ganttTasks.length : null,
      ganttTasksNow: pb ? pb.ganttTasks.length : null,
    });
  }

  rows.sort((x, y) => x.title.localeCompare(y.title, 'ru'));
  return rows;
}

/** Текущее состояние из живых данных — с пересчётом прогресса по правилам приложения. */
export function payloadFromLive(
  projects: EditableProject[],
  ganttItems: GanttItem[],
): ReviewSnapshotPayload {
  const projectsNorm = projects.map((p) => ({
    ...p,
    progress: computeProjectProgress(p.ganttTasks),
  }));
  return {
    schemaVersion: SNAPSHOT_SCHEMA_VERSION,
    projects: JSON.parse(JSON.stringify(projectsNorm)) as EditableProject[],
    ganttItems: JSON.parse(JSON.stringify(ganttItems)) as GanttItem[],
  };
}

export function ganttItemsChangedCount(a: GanttItem[], b: GanttItem[]): number {
  if (a.length !== b.length) return Math.max(a.length, b.length);
  const ser = (g: GanttItem) =>
    `${g.id}|${g.title}|${g.start}|${g.end}|${g.status}|${g.phaseId}|${g.projectId}`;
  const setA = new Set(a.map(ser));
  let n = 0;
  for (const g of b) {
    if (!setA.has(ser(g))) n++;
  }
  return n;
}
